import { each, find, isEqual } from 'lodash';
import * as React from 'react';


import { Delete } from '@mui/icons-material';
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { sortEntries } from '../../../util/entries';
import { DropdownV2 } from '../../assorted/dropdown-v2';
import { BmcSelector } from '../bmc-selector/bmc-selector';
import ThumbsDownIcon from '../../../../icons/ThumbsDownIcon';
import ThumbsUpIcon from '../../../../icons/ThumbsUpIcon';
import DeleteOutlinedIcon from '../../../../icons/DeleteOutlinedIcon';

interface Refs {
    [key: string]: React.Component<any, any>;
    stateSelector: any;
    hypothesisSelector: any;
    hypothesiCatsSelector: any;
}

interface Props {
    findingID: string;
    baseAtomBridge: any;
    categories: { [key: string]: CanvasCategory };
    entries: { [key: string]: CanvasEntry };
    isMenuOpen?: boolean;
    links?: HypothesisSupports[];
    workingLinks?: { key: string, category: string, hypothesisKey: string, supports: boolean }[];
    otherTakenEntryIDs?: string[];
    valid: boolean;
    base: any;
    open?: (isOpen: boolean) => void;
    save: (key: string, category: string, entry: string, supports: boolean) => void;
    remove: (key: string) => void;

    setValid: (isValid: boolean) => void;

    isSaving: boolean;

    linkID: string;
    category: string;
    entry: string;
    supports: boolean;
    categoryLabel?: string;

    team: TeamState;
    appState: AppState;

    shouldBeForSub?: boolean;
    canvas?:any;
    theme: Theme;
}

interface State {
    inHover: boolean;
    dropdownBuffer: any;
    shouldBeForSub: boolean; // Assign the prop to this on mount
    // however if the selected entry proves to be a sub
    // use it in the state so we can override it locally
    isDropdownOpen: boolean;
    selectedOption: boolean | null;
}

const ACTION_BAR_HEIGHT = '30px';

export class HypothesisFindingSupport extends React.Component<Props, State> {
    refs: Refs;
    dropdownRef: React.RefObject<HTMLDivElement>;
    state: State = {
        inHover: false,
        shouldBeForSub: false,
        dropdownBuffer: undefined,
        isDropdownOpen: false,
        selectedOption: null,
    }
    constructor(props) {
      super(props);
      this.dropdownRef = React.createRef();
    }
    
    // Handle determining where our current state comes from
    getData = (props: Props) => {
        return {
            doesSupport: props.supports,
            currentEntry: props.entry,
            currentCategory: props.category
        }
    }

    componentDidMount() {
        const {
            currentEntry
        } = this.getData(this.props);

        if (currentEntry === undefined) {

            try {
                if (this.refs.hypothesisSelector) {
                    this.refs.hypothesisSelector.open();
                }
            }
            catch (e) { }

        }
        else {

        }
        document.addEventListener("mousedown", this.handleClickOutside);
    }
    componentWillUnmount() {
      document.removeEventListener("mousedown", this.handleClickOutside);
    }
  
    handleClickOutside = (event) => {
      if (
        this.dropdownRef.current &&
        !this.dropdownRef.current.contains(event.target)
      ) {
        this.setState({ isDropdownOpen: false });
      }
    };
    shouldComponentUpdate(nextProps, nextState) {
        if (!isEqual(this.props, nextProps)) return true;
        if (!isEqual(this.state, nextState)) return true;
        return false;
    }

    getCurrentEntry = () => {
        const {
            currentEntry
        } = this.getData(this.props);

        return currentEntry;
    }

    checkExisting = (entryID) => {
        if (this.props.entry === entryID) return false;
        
        if (this.props.otherTakenEntryIDs === undefined) {
            return false;
        }

        if (this.props.otherTakenEntryIDs.indexOf(entryID) > -1) {
            return true;
        }

        return false;
    }

    getEntriesForCurrentCategory = () => {
        const {
            currentCategory,
        } = this.getData(this.props);

        let buffer = [];

        for (let i = 0; i < this.props?.canvas?.categories?.length; i++) {
            const category = this.props.canvas.categories[i];

            if (category?.categoryID === currentCategory) {
                for (let e = 0; e < category?.entries?.length; e++) {
                    const entry = category.entries[e];

                    if (
                        (entry.state?.name
                            ? entry.state?.name !== "Deleted"
                            : entry.state !== "Deleted") &&
                        entry.entry &&
                        entry.entry !== ""
                    ) {
                        // also make sure it isn't in the known set
                        if (this.checkExisting(entry.entryID) === false) {
                            buffer.push({
                                key: entry?.entryID,
                                value: entry?.entry,
                                sortOrder: entry?.sortOrder
                            });
                        }
                    }
                }
            }
        }

        return sortEntries(buffer);
    }

    getCategoryByAbbr = (abbrv): CanvasCategory => {
        const {
            categories
        } = this.props;

        let foundCategory = undefined;

        each(categories, category => {
            if (category.abbreviation === abbrv) {
                foundCategory = category;
            }
        });

        return foundCategory;
    }

    save = (entry, category, supports) => {
        const { save } = this.props;

        save(this.props.linkID, category, entry, supports);
    }

    remove = () => {
        this.props.remove(this.props.linkID);
    }

    findCategoryTitle = (categoryID) => {
        const {
            categories
        } = this.props;

        if (categoryID === undefined) return undefined;

        let found = undefined;

        each(categories, category => {
            if (category?.categoryID === categoryID) {
                found = category?.name;
            }
        });

        return found;
    }

    getCategoryOffCurrent = () => {
        const {
            categories,
            category
        } = this.props;

        return find(categories, (_category) => {
            return _category?.categoryID === category
        });
    }

    findCurrentCategory = () => {
        let { categories, entries } = this.props;

        const {
            currentCategory,
            currentEntry
        } = this.getData(this.props);

        let knownCategory = currentCategory;

        each(categories, category => {
            if (category?.entries?.length) {

                each(entries, entry => {
                    if (category.entries.indexOf(entry?.entryID) > -1 &&
                        (entry.entryID === currentEntry)) {
                        knownCategory = category?.categoryID;
                    }
                });
            }
        });

        return knownCategory;
    }

    handleSupportControlClick = (supportChange) => {
        const {
            doesSupport,
            currentEntry,
            currentCategory
        } = this.getData(this.props);

        this.save(currentEntry, currentCategory, supportChange);
        this.setState({ selectedOption: supportChange, isDropdownOpen: false });
      }

      renderSupportControl = () => {
        const { doesSupport } = this.getData(this.props);
        const {theme} = this.props
        const { selectedOption, isDropdownOpen } = this.state;
        const getSupportStatus = () => {
          if (doesSupport!==undefined && selectedOption !== null) {
            return {
              label: selectedOption ? "Supports" : "Does not Support",
              icon: selectedOption ? (
                <ThumbsUpIcon sx={{ color: theme.custom.validInsightGreen }} />
              ) : (
                <ThumbsDownIcon sx={{ color: theme.custom.invalidInsightRed }} />
              ),
            };
          }
          if (doesSupport!==undefined && (doesSupport === true || doesSupport === 1 )) {
            return {
              label: "Supports",
              icon: <ThumbsUpIcon sx={{ color: theme.custom.validInsightGreen }} />,
            };
          }
          if (doesSupport!==undefined && (doesSupport === false || doesSupport === 0)) {
            return {
              label: "Does not Support",
              icon: <ThumbsDownIcon sx={{ color: theme.custom.invalidInsightRed }} />,
            };
          }
          return { label: "Evidence Status", icon: null };
        };
      
        const { label, icon } = getSupportStatus();
        return (
          <div style={{ position: "relative" }} ref={this.dropdownRef}>
            <Tooltip title={label} placement="top">
              <Button
                onClick={() =>
                  this.setState({ isDropdownOpen: !isDropdownOpen })
                }
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  minWidth:'100px',
                  maxWidth: "150px",
                  padding: "5px 8px 5px 9px",
                  border: `1px solid ${theme.custom.borderGrey}`,
                  color: "#000000",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontWeight: "400",
                  // ".MuiButton-startIcon": { marginLeft: "0px" },
                }}
                startIcon={icon}
                endIcon={<KeyboardArrowDownOutlinedIcon />}
                variant="outlined"
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </span>
              </Button>
            </Tooltip>
            {isDropdownOpen && (
              <div
                style={{
                  padding: "5px",
                  backgroundColor: "#ffffff",
                  gap: "3px",
                  position: "absolute",
                  // width:'100%',
                  width: "155px",
                  zIndex: 11,
                  borderRadius: "3px",
                  border: `1px solid ${theme.custom.borderGrey}`,
                  boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.10)",
                }}
              >
                <Box
                  onClick={() => this.handleSupportControlClick(true)}
                  title="Supports"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "5px 0px",
                    backgroundColor:
                      selectedOption === true ? "#007bff" : "transparent",
                    color: selectedOption === true ? "#ffffff" : "inherit",
                    ":hover": {
                      backgroundColor: "#f3fbff",
                      cursor: "pointer",
                      color: "inherit",
                    },
                  }}
                >
                  <ThumbsUpIcon sx={{ color: theme.custom.validInsightGreen }}/>
                  <span style={{ padding: "0px 3px" }}>Supports</span>
                </Box>
                <Box
                  onClick={() => this.handleSupportControlClick(false)}
                  title="Does Not Support"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "5px 0px",
                    backgroundColor:
                      selectedOption === false ? "#007bff" : "transparent",
                    color: selectedOption === false ? "#ffffff" : "inherit",
                    ":hover": {
                      backgroundColor: "#f3fbff",
                      cursor: "pointer",
                      color: "inherit",
                    },
                  }}
                >
                  <ThumbsDownIcon sx={{ color: theme.custom.invalidInsightRed }}/>
                  <span style={{ padding: "0px 3px" }}>Does not Support</span>
                </Box>
              </div>
            )}
          </div>
        );
      };
      

    handleCategoryClick = (categoryID) => {
        const {
            doesSupport
        } = this.getData(this.props);

        this.save(undefined, categoryID, doesSupport);

        // Try to open the entry selector
        if (this.refs.hypothesisSelector) {
            //this.refs.hypothesisSelector.open();
            this.refs.hypothesisSelector.openDropdown();
        }
    }

    renderLinkActionBar = () => {
        const {
            currentCategory,
            currentEntry,
            doesSupport
        } = this.getData(this.props);
        const { theme } = this.props;
        const {
            dropdownBuffer
        } = this.state;

        const knownCategory = this.findCategoryTitle(currentCategory);

        let placeholderText = "Select a hypothesis";

        if (knownCategory) {
            placeholderText = "Select a " + knownCategory;
        }

        let tooltipValue = dropdownBuffer;
        const allEntries = this.getEntriesForCurrentCategory();
        
        if (dropdownBuffer !== undefined) {
          allEntries.some((entry, index) => {
            if (entry.key === dropdownBuffer) {
              tooltipValue = entry.value;
              return true; // Exit the loop
            }
            return false; // Continue the loop
          });
        }
        else if(dropdownBuffer === undefined && currentEntry!==undefined){
          allEntries.some((entry, index) => {
            if (entry.key === currentEntry) {
              tooltipValue = entry.value;
              return true; // Exit the loop
            }
            return false; // Continue the loop
          });
        }

        return (
          <Tooltip title={(tooltipValue !== undefined && tooltipValue!==dropdownBuffer) ? tooltipValue : null} placement="top">
            <span
              style={{
                height: ACTION_BAR_HEIGHT,
              
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#000000",
              }}
            >
              <DropdownV2
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "#000000",
                }}
                v3Position={true}
                selectWillClose={true}
                isMenuOpen={this.props.isMenuOpen}
                // controllerWidth={"200px"}
                onChange={(e, t) => {
                  this.setState({
                    dropdownBuffer: t,
                    isDropdownOpen: true,
                  });

                  this.save(t, currentCategory, undefined);
                }}
                disabled={currentCategory ? false : true}
                ref="hypothesisSelector"
                placeholder={placeholderText}
                items={this.getEntriesForCurrentCategory()}
                selected={currentEntry}
                theme={theme}
              />
            </span>
          </Tooltip>
        );
    };

    renderLinkText = () => {
        const {
            currentEntry
        } = this.getData(this.props);

        if (currentEntry === undefined) return <span></span>

        const {
            entries
        } = this.props;

        const linkTextStyle = {
            display: 'block',
            width: '100%',
            height: '100%',
            marginTop: '10px'
        }

        let entryContent = '';

        each(entries, entry => {
            if (entry?.entryID === currentEntry) {
                entryContent = entry.entry;
            }
        })

        return <span style={linkTextStyle}>{entryContent}</span>
    }

    renderCategoryControl = () => {
        const {
            currentCategory
        } = this.getData(this.props);

        // Render the BMC selector widget
        return <BmcSelector
            canvasData={this.props.canvas}
            appState={this.props.appState}
            team={this.props.team}
            renderPlaceholder={false}
            handleCategoryClick={this.handleCategoryClick}
            currentCategory={currentCategory}
            categories={this.props.categories}
            baseAtomBridge={this.props.baseAtomBridge}
            abbreviationIsSub={this.state.shouldBeForSub}
        />
    }

    renderTrashControl = () => {
        const {
            currentCategory
        } = this.getData(this.props);

        const {
            inHover
        } = this.state;

        // if (!inHover) return <div></div>

        if (currentCategory === undefined) return <div></div>

        // return <Box></Box>

        return <Tooltip title="Remove Insight Link" placement="top">
            <IconButton sx={{  padding:'4px 2px 4px 5px' }} onClick={() => this.remove()}>
            <DeleteOutlinedIcon sx={{
              color: "gray",
              fill:"none",
              "&:hover": {
                color: "#1976D2",
              },
            }}/>
            </IconButton>
        </Tooltip>
    }

    renderFullControlSet = () => {
        const marginRight = '10px';
        const firstWaveWidth = 'inline-block';
        
        return (
          <Stack
            sx={{ paddingTop: "3px", alignItems: "center", gap: "10px" }}
            direction={"row"}
            spacing={0}
          >
            <Box
              sx={{
                display: "flex",
                gap: "5px",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box sx={{ minWidth:'80px',maxWidth:'26%'}}>{this.renderCategoryControl()}</Box>
              {
                this.props.category!==undefined && <Box sx={{ minWidth:'140px',flex:'1'}}>{this.renderLinkActionBar()}</Box>
              }
              {
                this.props.category!==undefined && this.props.entry!==undefined && <Box sx={{ }}>
                {this.renderSupportControl()}
                </Box>
              }
              
            </Box>
    
            <Box sx={{}}>{this.renderTrashControl()}</Box>
          </Stack>
        );
    }

    getIsValid = () => {
        const entry = this.getCurrentEntry();

        return !(entry === undefined);
    }

    render() {
        // This is a 2 column component
        // The first column is supports/doesn't support
        // Then a info-graphic to select the category

        return <span
            onMouseEnter={() => { this.setState({ inHover: true }) }}
            onMouseLeave={() => { this.setState({ inHover: false }) }}
            style={{ display: 'block', marginBottom: '5px', backgroundColor: this.props.valid ? 'white' : 'rgba(255,0,0,0.2)' }}>
            {this.renderFullControlSet()}
        </span>
    }
}
